import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { OrangeTextButton } from "../../../common/OrangeTextButton";
import {
  restartSites,
  useBulkRestartStore,
} from "./SitesPageUtils/bulkSyncRestart";

/**
 * "Restart now or wait for next arm?" after a bulk SYNC (ENG-2800). Opened by
 * handleBulkGroupAction through useBulkRestartStore once every selected site
 * has been synced.
 */
export const BulkSyncRestartDialog = () => {
  const { open, restart, flex, unconfirmed, close } = useBulkRestartStore();
  const [restarting, setRestarting] = useState<boolean>(false);

  const handleRestart = async () => {
    setRestarting(true);
    const failed = await restartSites(restart);
    setRestarting(false);
    close();
    if (failed.length > 0) {
      alert(
        `These sites could not be restarted: ${failed.join(
          ", "
        )}. Open each site's About page to restart it.`
      );
    }
  };

  const siteList = (names: string[]) => (
    <ul className="list-disc pl-[20px] mt-[6px] max-h-[160px] overflow-y-auto">
      {names.map((name) => (
        <li key={name}>{name}</li>
      ))}
    </ul>
  );

  return (
    <Dialog open={open} aria-labelledby="bulk-sync-restart-title">
      <DialogTitle id="bulk-sync-restart-title">
        {restart.length > 0 ? "Restart running sites?" : "Sync finished"}
      </DialogTitle>
      <DialogContent>
        {restart.length > 0 && (
          <DialogContentText component="div">
            {restart.length === 1
              ? "1 synced site is armed and still running its previous settings:"
              : `${restart.length} synced sites are armed and still running their previous settings:`}
            {siteList(restart.map((site) => site.name))}
            Restart them now to apply the changes, or wait and the changes apply
            the next time each site arms.
          </DialogContentText>
        )}
        {flex.length > 0 && (
          <DialogContentText component="div" className="mt-[12px]">
            These sites use flex schedules and can&apos;t be restarted from
            here. To apply now, open each site&apos;s About page and restart the
            schedules you want:
            {siteList(flex.map((site) => site.name))}
          </DialogContentText>
        )}
        {unconfirmed.length > 0 && (
          <DialogContentText component="div" className="mt-[12px]">
            We couldn&apos;t confirm these sites finished syncing, so they
            weren&apos;t included. Check each site&apos;s About page:
            {siteList(unconfirmed.map((site) => site.name))}
          </DialogContentText>
        )}
      </DialogContent>
      <DialogActions>
        {restart.length > 0 ? (
          <>
            <OrangeTextButton
              onClick={close}
              text="Wait for next arm"
              disabled={restarting}
            />
            <OrangeTextButton
              onClick={handleRestart}
              text={restarting ? "Restarting…" : "Restart now"}
              disabled={restarting}
            />
          </>
        ) : (
          <OrangeTextButton onClick={close} text="OK" disabled={false} />
        )}
      </DialogActions>
    </Dialog>
  );
};
